// src/components/CreateVideoAdmin.js
import React, { useState, useContext, useEffect } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { Container } from 'react-bootstrap'
import { Navigate } from 'react-router-dom'
import axios from 'axios'
import { getAccessToken } from '../services/AuthService'
import { UserContext } from '../context'
import { EditorState, convertToRaw } from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
import { toast } from 'react-toastify'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import 'react-toastify/dist/ReactToastify.css'

import CreateCategory from './CreateCategory'
import FullscreenDraftEditor from './FullscreenDraftEditor'
import FontFamilyControl from './FontFamilyControl'
import { EDITOR_TOOLBAR, STYLE_MAP } from './editorToolbar'
import './CreateVideoAdmin.css'

/* ------------------------ Category dropdown ------------------------ */
const CategoryList = ({ getCategory, field, form, ...props }) => {
  const [categories, setCategories] = useState([])
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = getAccessToken()
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/category_list/`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
        )
        setCategories(data || [])
      } catch (err) {
        toast.error(`Error obteniendo categorías: ${err.message}`)
      }
    }
    fetchCategories()
  }, [getCategory])

  return (
    <select {...field} {...props}>
      <option value="" disabled>— Selecciona una categoría —</option>
      {categories.map((c) => (
        <option key={c.id} data-key={c.id} value={c.title}>
          {c.title}
        </option>
      ))}
    </select>
  )
}

/* ---------------------------- Main form ---------------------------- */
const CreateVideoAdmin = ({ isLoggedIn }) => {
  const [state] = useContext(UserContext)
  const [isSubmitted, setSubmitted] = useState(false)

  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [refreshCategories, setRefreshCategories] = useState(false)

  // Single source of truth for the editor (inline + fullscreen)
  const [descState, setDescState] = useState(() => EditorState.createEmpty())
  const [activeFont, setActiveFont] = useState(null) 
  const [descError, setDescError] = useState('')
  const [fsOpen, setFsOpen] = useState(false)

  const [imagePreview, setImagePreview] = useState(null)
  useEffect(() => () => imagePreview && URL.revokeObjectURL(imagePreview), [imagePreview])

  const initialValues = {
    title: '',
    image: '',
    url: '',
    free: false,
    categories: '',
  }

  const handleDescChange = (next) => {
    if (activeFont) {
      const sel = next.getSelection()
      if (sel.isCollapsed()) {
        // keep the chosen font “sticky” while typing
        let override = next.getCurrentInlineStyle()
        // remove any other font styles from override before adding our activeFont
        Object.keys(STYLE_MAP).forEach(k => { override = override.remove(k) })
        if (!override.has(activeFont)) {
          override = override.add(activeFont)
          next = EditorState.setInlineStyleOverride(next, override)
        }
      }
    }
    setDescState(next)
  }

  const vimeoEmbedPattern = /player\.vimeo\.com\/video\/\d+/i

  const validate = () => {
    const errors = {}
    if (!descState.getCurrentContent().hasText()) {
      errors.__desc = 'x' // sentinel
      setDescError('La descripción es requerida')
    } else {
      setDescError('')
    }
    return errors
  }

  const handleSubmit = async (values) => {
    // extra guard for description
    if (!descState.getCurrentContent().hasText()) {
      setDescError('La descripción es requerida')
      return
    }

    // validate rest (quickly)
    const localErrors = {}
    if (!values.title) localErrors.title = 'El título es requerido'
    if (!values.url || !vimeoEmbedPattern.test(values.url)) {
      localErrors.url = 'Pega el código de inserción (embed) de Vimeo válido'
    }
    if (!values.categories) localErrors.categories = 'Selecciona una categoría'
    if (!values.image) localErrors.image = 'La portada (imagen) es requerida'
    if (Object.keys(localErrors).length) return

    try {
      const content = JSON.stringify(convertToRaw(descState.getCurrentContent()))
      const select = document.getElementById('admin-video-category')
      const selectedOption = select?.options[select.selectedIndex]
      const optionAttributes = Array.from(selectedOption?.attributes || []).reduce((acc, a) => {
        acc[a.name] = a.value
        return acc
      }, {})

      const fd = new FormData()
      fd.append('title', values.title)
      fd.append('image', values.image)
      fd.append('description', content)
      fd.append('categories', JSON.stringify(optionAttributes))
      fd.append('url', values.url)
      fd.append('free', values.free)

      const token = getAccessToken()
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/video_detail/`,
        fd,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
      )
      toast.success('Video creado con éxito 🎉')
      setSubmitted(true)
    } catch (err) {
      console.error(err)
      toast.error(`Error creando video: ${err.message}`)
    }
  }

  if (!isLoggedIn || (state.user && !state.user.is_staff)) return <Navigate to="/log-in" />
  if (isSubmitted) return <Navigate to="/videos" />

  return (
    <Container className="video-admin">
      <div className="va__header">
        <h1>Crear nuevo video</h1>
        <p>Completa la información del video, agrega una portada y el código de embebido de Vimeo.</p>
      </div>

      <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
        {({ isSubmitting, setFieldValue, values }) => (
          <>
            <Form className="va__grid">
              {/* LEFT: form sections */}
              <div className="va__col">
                {/* Basics */}
                <section className="va__card">
                  <h2 className="va__card-title">Básicos</h2>

                  <div className="va__field">
                    <label htmlFor="title">Título del video</label>
                    <Field id="title" name="title" type="text" placeholder="Ej. Vinyasa Flow – Energía Matutina" />
                    <ErrorMessage name="title" component="div" className="va__error" />
                  </div>

                  <div className="va__field">
                    <label htmlFor="image">Portada (imagen)</label>
                    <div className="va__uploader">
                      <input
                        id="image"
                        name="image"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.currentTarget.files?.[0]
                          setFieldValue('image', file || '')
                          const url = file ? URL.createObjectURL(file) : null
                          setImagePreview(prev => {
                            if (prev) URL.revokeObjectURL(prev)
                            return url
                          })
                        }}
                      />
                      <div className="va__uploader-help">JPG o PNG. Recomendado 1280×720 o superior.</div>
                    </div>
                    <ErrorMessage name="image" component="div" className="va__error" />
                  </div>

                  <div className="va__field va__switch">
                    <label className="form-switch">
                      <Field type="checkbox" name="free" />
                      <span className="slider" aria-hidden="true"></span>
                      <span className="switch-label">Marcar como video gratuito</span>
                    </label>
                  </div>
                </section>

                {/* Descripción */}
                <section className="va__card">
                  <div className="va__card-title-row">
                    <h2 className="va__card-title">Descripción</h2>
                    <div className="va__editor-controls">
                      <FontFamilyControl
                        editorState={descState}
                        setEditorState={setDescState}
                        setActiveFont={setActiveFont}
                      />
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setFsOpen(true)}
                      >
                        Pantalla completa
                      </button>
                    </div>
                  </div>

                  {!fsOpen && (
                    <>
                      <Editor
                        editorState={descState}
                        onEditorStateChange={handleDescChange}
                        wrapperClassName="va__editor-wrapper"
                        editorClassName="va__editor"
                        toolbarClassName="va__editor-toolbar"
                        toolbar={EDITOR_TOOLBAR}
                        customStyleMap={STYLE_MAP}
                        editorStyle={{ color: '#111' }}
                      />
                      {descError && <div className="va__error">{descError}</div>}
                      <div className="va__hint">Describe la clase, el objetivo, materiales, duración, etc.</div>
                    </>
                  )}
                </section>

                {/* Video URL */}
                <section className="va__card">
                  <h2 className="va__card-title">Video (Vimeo)</h2>
                  <div className="va__field">
                    <label htmlFor="url">Pega el código de inserción (embed) de Vimeo</label>
                    <Field
                      as="textarea"
                      id="url"
                      name="url"
                      rows="5"
                      placeholder={`Ej. <iframe src="https://player.vimeo.com/video/123456789" ...></iframe>`}
                    />
                    <ErrorMessage name="url" component="div" className="va__error" />
                    <div className="va__helper">
                      Asegúrate de que <strong>src</strong> contenga <code>https://player.vimeo.com/video/&lt;ID&gt;</code>.
                    </div>
                  </div>
                </section>

                {/* Category */}
                <section className="va__card">
                  <div className="va__card-title-row">
                    <h2 className="va__card-title">Categoría</h2>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => setShowCategoryModal(true)}
                    >
                      + Nueva categoría
                    </button>
                  </div>

                  <div className="va__field">
                    <label htmlFor="admin-video-category">Selecciona una categoría</label>
                    <Field
                      as="select"
                      id="admin-video-category"
                      name="categories"
                      component={CategoryList}
                      className="va__select"
                      getCategory={refreshCategories}
                    />
                    <ErrorMessage name="categories" component="div" className="va__error" />
                  </div>
                </section>

                {/* Actions */}
                <div className="va__actions">
                  <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Guardando…' : 'Crear video'}
                  </button>
                </div>
              </div>

              {/* RIGHT: sticky preview */}
              <aside className="va__preview">
                <div className="va__preview-card">
                  <div className="va__preview-media">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Vista previa" />
                    ) : (
                      <div className="va__preview-placeholder">Vista previa de portada</div>
                    )}
                    {values.free && <span className="va__badge">Gratis</span>}
                  </div>
                  <div className="va__preview-body">
                    <h3>{values.title || 'Título del video'}</h3>
                    <div className="va__preview-meta">
                      {values.categories || 'Sin categoría'}
                    </div>
                    <div className="va__preview-note">
                      La vista previa se actualiza a medida que completas el formulario.
                    </div>
                  </div>
                </div>
              </aside>
            </Form>

            {/* Fullscreen Editor */}
            {fsOpen && (
               <FullscreenDraftEditor
                editorState={descState}
                onChange={handleDescChange}      
                onClose={() => setFsOpen(false)}
                setActiveFont={setActiveFont}    
              />
            )}
 
            {/* Modal categorías */}
            {showCategoryModal && (
              <CreateCategory
                onClose={() => setShowCategoryModal(false)}
                setGetCategories={setRefreshCategories}
              />
            )}
          </>
        )}
      </Formik>
    </Container>
  )
}

export default CreateVideoAdmin
